export type Preset = {
  id: string;
  label: string;
  location: string;
  years: readonly number[];
};

export const pt = {
  appTitle: 'Máquina do Tempo · Panorâmica',
  appTagline:
    'Descreva um lugar e uma época. Geramos um panorama 360° imersivo para você explorar.',
  promptLabel: 'Descreva um momento no tempo',
  promptHelper:
    'Descreva qualquer momento da história (ou do futuro). Geramos um panorama 360° imersivo que você pode explorar arrastando.',
  promptPlaceholder: 'ex.: Cristóvão Colombo pisando em uma praia do Caribe ao amanhecer',
  generateCta: 'Gerar panorama',
  generateCtaLoading: 'Viajando…',
  viewerHint: 'Arraste para olhar em volta · Role para dar zoom · Enter para gerar',
  apiKeyLabel: 'Chave da API OpenAI',
  apiKeyPlaceholder: 'sk-...',
  apiKeyHelper:
    'Armazenada localmente no seu navegador. Usada apenas para chamar a API de imagens.',
  apiKeyShow: 'Mostrar chave',
  apiKeyHide: 'Ocultar chave',
  apiKeySave: 'Salvar chave',
  apiKeyAdd: 'Adicionar chave',
  apiKeyReplace: 'Trocar',
  apiKeyMissingError: 'Por favor, insira sua chave da API OpenAI primeiro.',
  viewerLoadError: 'Falha ao carregar a imagem gerada no visualizador.',
  historyTitle: 'Histórico de viagens',
  historyEmpty: 'Suas viagens anteriores aparecerão aqui.',
  historyClear: 'Limpar',
  historyNoPreview: 'Sem preview',
  presetsTitle: 'Sugestões',
  costDisclosureLabel: 'Custo estimado por viagem',
  costDisclosureValue: '≈ US$ 0,85 – 1,20 (5 épocas em alta qualidade)',
  shareCta: 'Compartilhar viagem',
  shareSuccess: 'Link copiado para a área de transferência!',
  shareError: 'Não foi possível compartilhar a viagem.',
  timeSliderLabel: 'Linha do tempo',
  viewerAriaLabel:
    'Visualizador de panorama 360° (use setas para girar, + e − para zoom, 0 para reiniciar)',
  sliderAriaLabel: 'Controle da linha do tempo (use setas para avançar entre épocas)',
  yearsLabel: 'Épocas',
  yearsAddEra: 'Adicionar época',
  yearsRemove: 'Remover',
  yearInputLabel: 'Ano da época',
  yearInputPlaceholder: 'Ex.: -500 ou 2026',
  yearInputSave: 'Salvar',
  yearInputCancel: 'Cancelar',
  qualityLabel: 'Qualidade',
  qualityHigh: 'Alta',
  qualityMedium: 'Padrão',
  locationLabel: 'Local',
  locationPlaceholder: 'ex.: Piazza Navona, Roma',
  keyStoredNote:
    'Sua chave nunca sai do navegador, exceto em requisições diretas para api.openai.com.',
  journeyInputError: 'Informe um local e ao menos duas épocas.',
  journeyNoneGeneratedError: 'Não foi possível gerar nenhuma das épocas solicitadas.',
  openAiStatusError: (status: number) => `Falha na OpenAI (${status}).`,
  rateLimited: (seconds: number | null) =>
    seconds
      ? `Limite da API OpenAI atingido. Aguarde ${seconds} segundos e tente novamente.`
      : 'Limite da API OpenAI atingido. Aguarde alguns segundos e tente novamente.',
  journeyGenerationError: 'Falha ao gerar a viagem.',
  partialLoadMsg: (loaded: number, total: number) => `${loaded} de ${total} épocas carregadas`,
  charCounterLabel: (used: number, max: number) => `${used} / ${max}`,
  promptTooLong: 'Prompt muito longo. Máximo de 2000 caracteres.',
  retryFailed: 'Tentar novamente',
  journeyNotFound: 'Viagem não encontrada.',
  galleryTemporarilyUnavailable:
    'Galeria temporariamente indisponível. Tente novamente em alguns instantes.',
  backToHome: 'Voltar ao início',
  footerCredits: 'Inspirado no protótipo de Pietro Schirano · Construído com GPT-Image-2',
} as const;

export const presets: readonly Preset[] = [
  {
    id: 'ancient-rome',
    label: 'Roma Antiga, 80 d.C.',
    location: 'Fórum Romano, Roma',
    years: [-500, 80, 400, 1500, 2026, 2500],
  },
  {
    id: 'tokyo-cyber',
    label: 'Tóquio Ciberpunk, 2087',
    location: 'Cruzamento de Shibuya, Tóquio',
    years: [1600, 1923, 1964, 2026, 2087, 2200],
  },
  {
    id: 'medieval-europe',
    label: 'Europa Medieval, 1347',
    location: 'Piazza San Marco, Veneza',
    years: [800, 1347, 1600, 1900, 2026, 2150],
  },
  {
    id: 'florence-renaissance',
    label: 'Florença Renascentista, 1503',
    location: 'Piazza della Signoria, Florença',
    years: [1200, 1503, 1800, 2026, 2200],
  },
  {
    id: 'london-victorian',
    label: 'Londres Vitoriana, 1888',
    location: 'Trafalgar Square, Londres',
    years: [1666, 1888, 1945, 2026, 2150],
  },
  {
    id: 'wild-west',
    label: 'Velho Oeste, 1875',
    location: 'Rua principal de Tombstone, Arizona',
    years: [1500, 1800, 1875, 1950, 2026, 2150],
  },
] as const;
